/* global AppSettings */
/**
 * The module handles the recently viewed items on the Lister + Detail pages.
 * It listens to add and render events and stores data into LocalStorage.
 * A client side template is used to render the items on page. The images
 * are loaded lazy via the ImageLazyLoad module.
 */
import $ from 'jquery';
import {gsap, CSSPlugin} from 'gsap/all';
import onWindowLoaded from 'components/utils/onWindowLoaded';
import MustacheWrapper from 'components/utils/MustacheWrapper';
import createLogger from '../logger/Logger';
import EventTypes from '../EventTypes';
import StorageKeys from '../utils/storage/StorageKeys';
import Storage from '../utils/storage/Storage';
import RvpManager from './RecentlyViewedProductsManager';
import TileDataProcessing from '../utils/tileDataProcessing';
import scrollTo from '../utils/scrollTo';
import getAllowedImageTypes from "../../../../../raw/src/core/util/AllowedProductTileImageTypes";
import handlePersonalisedPrice from "../utils/productPersonalisedPrice";
import ImageLazyLoad from '../utils/ImageLazyLoad';
import TrackingObserver from '../productLister--v2/TrackingObserver';
import device from '../utils/device';
import matchBreakpoint from '../utils/matchBreakpoint';
import SlideCarousel from '../carousels/SlideCarousels';
import {getCSRFToken} from '../utils/CsrfUtils';
import FiberRanking from '../fiberRanking/FiberRanking';

const Logger = createLogger('RecentlyViewedProducts');
gsap.registerPlugin(CSSPlugin);

const SKU_LIST_URL = `/${AppSettings.locale}/recentlyViewedProducts/filter/`;
const SERVER_SYNC_EXPIRY = Date.now() - 24 * 60 * 60 * 1000; // 1 day

const Selectors = {
  TEMPLATE: '#rvpProductTile-template',
  CONTAINER: '.js-recentlyViewedProducts',
  CAROUSEL: '.js-slideCarousel--recentlyViewedProducts',
  CAROUSEL_CONTENTS: '.js-recentlyViewedProducts-inner',
  PRODUCT: '.js-productListerTeaser, .js-productTile',
  LAZY_IMAGE: 'img.is-lazy',
  CLOSE_COMPONENT: '.js-closeSliderCarousel',
};

const Classes = {
  HIDDEN: 'is-hidden',
  CAROUSEL_INITIALIZED: 'js-slideCarousel-initialized',
};

const v2Template = document.querySelector(Selectors.TEMPLATE);
const template = v2Template?.textContent;
const rvpManager = RvpManager.getInstance();

/**
 * Recently Viewed Products carousel class.
 */
class RecentlyViewedProducts {
  /**
   * Constructs the object.
   */
  constructor() {
    const carouselComponent = document.querySelector(Selectors.CAROUSEL);
    this.initialRenderPerformed = false;
    this.container = document.querySelector(Selectors.CONTAINER);
    this.oneRowOnMobile =
      carouselComponent && carouselComponent.hasAttribute('data-one-row-on-mobile');

    !!this.container && this.bindEvents();

    document.jq.trigger(EventTypes.RECENTLY_VIEWED_PRODUCTS_INITIALIZED);
  }

  /**
   * Post process any product data in need of processing.
   *
   * @param {array} products - Array of products.
   * @returns {array} Array of products.
   */
  postProcessProducts(products) {
    return products.map((p, index) => {
      const product = handlePersonalisedPrice(p);

      if (product.signings && product.signings.length) {
        let noPositionCounter = 0;

        product.signings = product.signings.map(signing => {
          if (
            !signing.cssClasses ||
            !signing.positionClass ||
            !signing.positionClass.includes('--position') ||
            signing.cssClasses.includes('--position6') ||
            signing.cssClasses.includes('--position5')
          ) {
            noPositionCounter++;
            signing.orderClass = `order--${noPositionCounter}`;
          }

          if (signing.productSigningBackgroundColour) {
            signing.backgroundColour = signing.productSigningBackgroundColour;
          }

          if (signing.productSigningTextColour) {
            signing.textColour = signing.productSigningTextColour;
          }

          if (signing.discountSigning) {
            signing.cssClasses = (signing.cssClasses || '') + ' productSigning--v2--sale';
          }

          return signing;
        });
      }

      return TileDataProcessing.postProcessProductData({
        product,
        index,
        currentPage: 0,
        pageSize: 0,
        list: TileDataProcessing.LIST_TYPES.RVP,
        isLazy: true,
        lqip: Boolean(AppSettings?.backgroundImages?.recentlyViewedBgImageEnabled),
      });
    });
  }

  /**
   * Returns the products to be rendered.
   *
   * @param {object|array|null} products - Product(s).
   * @param {string} [excludeSku] Optional product sku in order
   * to exclude product from the returned products array.
   * @param {boolean} excludeLast
   * @returns {array} Array of products.
   */
  getProducts(products, excludeSku, excludeLast) {
    let result;

    if (products) {
      result = Array.isArray(products) ? products : [products];
    } else {
      result = rvpManager.getProducts(excludeSku, excludeLast);
    }

    return this.postProcessProducts(result);
  }

  /**
   * Renders the recently viewed products.
   *
   * @param {array} products - Array of items to render.
   */
  render(products) {
    Logger.time('render');
    Logger.debug('render');
    let slideCarousel;
    let innerContainer;
    let signingEnabled;

    if (this.container) {
      slideCarousel = this.container.querySelector(Selectors.CAROUSEL);
      innerContainer = this.container.querySelector(Selectors.CAROUSEL_CONTENTS);
    }

    products.forEach(product => {
      if (product.signings && product.signings.length) {
        let noPositionCounter = 0;

        // original order is required for setting correct classes futher down the road
        product.signings
          .reverse()
          .map(signing => {
            if (
              !signing.positionClass ||
              !signing.positionClass.includes('--position') ||
              signing.positionClass.includes('--position6') ||
              signing.positionClass.includes('--position5')
            ) {
              noPositionCounter++;
              signing.orderClass = `order--${noPositionCounter}`;
            }

            return signing;
          })
          .reverse();
      }
    });

    if (innerContainer && slideCarousel) {
      signingEnabled = slideCarousel.dataset.signingEnabled === 'true';

      this.getHtml(products, signingEnabled)
        .then(viewsHtml => {
          if (/[\S]+/.test(viewsHtml)) {
            if (products) {
              innerContainer.insertAdjacentHTML('afterbegin', viewsHtml);
            } else {
              innerContainer.innerHTML = viewsHtml;
            }

            if (device.isMobile && !matchBreakpoint('xsmall', device.screenWidth)) {
              if (this.oneRowOnMobile) {
                SlideCarousel.attachTo(slideCarousel.jq);
              } else {
                ImageLazyLoad.observe(`${Selectors.CAROUSEL} ${Selectors.LAZY_IMAGE}`);
                new TrackingObserver(innerContainer);
              }
            } else if (slideCarousel.classList.contains(Classes.CAROUSEL_INITIALIZED)) {
              slideCarousel.jq.trigger(EventTypes.SLIDE_CAROUSEL_RENDER_REQUEST);
            } else {
              SlideCarousel.attachTo(slideCarousel.jq);
            }

            document.jq.trigger(EventTypes.RECENTLY_VIEWED_PRODUCTS_RENDER_SUCCESS);
          } else {
            document.jq.trigger(EventTypes.RECENTLY_VIEWED_PRODUCTS_NO_PRODUCTS_FOUND);
          }

          this.initialRenderPerformed = true;
          this.container.classList.remove(Classes.HIDDEN);
          Logger.timeEnd('render');
        })
        .finally(() => {
          this.container
            .querySelector('.js-slideCarousel-slidesContainer')
            .classList.remove('is-pulse');
        })
        .catch(reason => {
          this.container.classList.add(Classes.HIDDEN);
          Logger.error(reason);
        });
    } else {
      document.jq.trigger(EventTypes.RECENTLY_VIEWED_PRODUCTS_NO_RENDER);
      Logger.timeEnd('render');
    }
  }

  /**
   * Returns whether or not the stored products server sync has expired.
   *
   * @returns {boolean} Sync expired (true), or not yet (false).
   */
  didStoredProductsSyncExpire() {
    const timestamp = Storage.getItem(StorageKeys.RECENTLY_VIEWED_PRODUCTS_TIMESTAMP) || false;

    return !timestamp || timestamp < SERVER_SYNC_EXPIRY;
  }

  /**
   * Removes zero prices products from the product array.
   *
   * @param {array} products - Array of products.
   * @returns {array} Array of products.
   */
  removeZeroPricedProducts(products) {
    const filtered = [];

    products.forEach(product =>
      /[1-9]+/gim.test(product.price?.value || product.basePrice)
        ? filtered.push(product)
        : rvpManager.removeProductFromStore(product.sku)
    );

    return filtered;
  }

  /**
   * Deprecated function to get product data for rendering.
   *
   * @param {array} products - Array of products.
   * @returns {array} Product data.
   * @deprecated
   */
  getDeprecatedProductData(products) {
    const filtered = this.removeZeroPricedProducts(products);

    return filtered.map(product => {
      product.image = product.image.replace(/https?:\/\//g, '');
      return product;
    });
  }

  /**
   * Function to get product data for rendering.
   *
   * @param {array} products - Array of products.
   * @returns {array} Product data.
   */
  getProductData(products) {
    return this.removeZeroPricedProducts(products);
  }

  /**
   * Deprecated method to get html based on an array of products.
   *
   * @param {array} products - Array of products.
   * @param {boolean} signingEnabled - If signings are enabled(true) or not(false).
   * @returns {string} Html string.
   */
  getDeprecatedProductHtml(products, signingEnabled) {
    return Promise.all(
      products.map(product => {
        return MustacheWrapper.render(template, {
          product,
          signingEnabled,
          signingBackground: product.signingBackground
            ? `background-color:${product.signingBackground};`
            : '',
          signingText: product.signingText ? `color:${product.signingText};` : '',
        });
      })
    );
  }

  /**
   * Removes any expired products.
   *
   * @param {array} products - Array of products.
   * @param {string} csrf - Csrf string.
   * @returns {promise} Remove expired products promise.
   */
  removeExpiredProducts(products, csrf) {
    if (!csrf || !this.didStoredProductsSyncExpire()) {
      return Promise.resolve(products);
    }

    return new Promise(resolve => {
      const skuList = products.map(product => product.sku);

      $.ajax({
        cache: false,
        type: 'POST',
        data: JSON.stringify({skuList}),
        contentType: 'application/json',
        headers: {CSRFToken: csrf},
        url: SKU_LIST_URL,
        success({skuList}) {
          if (!skuList || !skuList.length) {
            return resolve(products);
          }

          skuList.forEach(sku => rvpManager.removeProductFromStore(sku));
          products = products.filter(({sku}) => skuList.indexOf(sku) === -1);
          resolve(products);
        },
        error: resolve.bind(this, products),
      });

      Storage.setItem(StorageKeys.RECENTLY_VIEWED_PRODUCTS_TIMESTAMP, Date.now());
    });
  }

  /**
   * Renders all products.
   *
   * @param {array} products - Array of products.
   * @param {boolean} signingEnabled - If signings are enabled(true) or not(false).
   * @returns {string} Render string.
   */
  renderProducts(signingEnabled, products) {
    products.forEach((product, index) => {
      if (product.productData) {
        const productData = JSON.parse(product.productData);
        productData.position = index + 1;
        product.productData = JSON.stringify(productData);
      } else {
        const isSpecialMaterialGroup = (materialGroup = '') =>
          ['Jeans', 'O 5 pockets', 'Col jeans'].includes(materialGroup);

        const createSrcSet = imageUrl =>
          imageUrl
            ? [320, 384, 512, 640, 768]
              .map(
                size =>
                  `${imageUrl
                    .replace('{{dimensions}}', `w_${size},ar_0.74`)
                    .replace('q_80', 'q_auto')} ${size}w`
              )
              .join(', ')
            : '';

        const findRelevantImage = key => {
          const image = product.simplifiedImages?.entry?.find(entry => entry?.key === key);

          if (image)
            return {
              imageType: image.key,
              altText: image.value?.altText,
              url: image.value?.url,
            };
        };

        const E01 = findRelevantImage('E01');
        const E02 = findRelevantImage('E02');
        const M02 = findRelevantImage('M02');

        const discountedPrice = product.price?.formattedValue || product.formattedBasePrice;
        const initialPrice = product.fromPrice?.formattedValue || product.formattedFromPrice;

        product.hasDiscountPrice = initialPrice && discountedPrice !== initialPrice;
        product.code = product.code?.toUpperCase();
        product.productData = {
          simplifiedImages: product.simplifiedImages?.entry?.reduce((acc, {key, value}) => {
            acc[key] = {
              imageType: key,
              altText: value.altText,
              url: value.url,
            };
            return acc;
          }, {}),

          availability: product.stockInformation?.stockMessage,
          basePrice: product.fromPrice?.value,
          comingSoon: product.comingSoon,
          finalPrice: product.price?.value,
          fit: product.fit,
          mainColor: product.mainColor?.description,
          mainColorHex: product.mainColor?.facetColorStyle,
          materialGroup: product.materialGroup,
          materialNumber: product.code,
          position: index + 1,
          programme: product.programEN,
          sale: product.hasDiscountPrice?.toString(),
          name: product.name,
          staticCategoryPath: product.staticCategoryPath,
          staticCategoryPathIds: product.staticCategoryPathIds,
          subTargetAudience: product.subTargetAudience,
        };
        product.productData = JSON.stringify(product.productData);
        product.stockQuantity = product.stockInformation?.stockQuantity;
        product.mcc = product.mccValue;
        product.color = product.colorFinishDescription;
        product.formattedBasePrice = product.price?.formattedValue;
        product.formattedFromPrice = product.fromPrice?.formattedValue;
        product.primaryImage = getAllowedImageTypes(AppSettings.allowedProductTilePrimaryImageTypes, product.simplifiedImages?.entry)?.value ?? E01;
        product.primaryImages = createSrcSet(product.primaryImage?.url);
        product.hoverImage = isSpecialMaterialGroup(product.materialGroup)
          ? getAllowedImageTypes(AppSettings.allowedProductTileSpecialMaterialHoverImageTypes, product.simplifiedImages?.entry)?.value ?? M02
          : getAllowedImageTypes(AppSettings.allowedProductTileHoverImageTypes, product.simplifiedImages?.entry)?.value ?? E02;
        product.secondaryImages = createSrcSet(product.hoverImage?.url);
        product.isLazy = true;
        product.image = product.primaryImage?.url?.replace('{{dimensions}}', 'h_1038');
        product.materialNumber = product.code;
      }
    });

    products = this.getProductData(products);

    return MustacheWrapper.render(template, {products});
  }

  /**
   * Returns html for the provided products.
   *
   * @param {array} products - Array of products.
   * @param {boolean} signingEnabled - If signings are enabled(true) or not(false).
   * @returns {promise} Html string promise.
   */
  getHtml(products, signingEnabled) {
    return getCSRFToken()
      .then(this.removeExpiredProducts.bind(this, products))
      .then(this.renderProducts.bind(this, signingEnabled))
      .catch(message => {
        Logger.error(message);
      });
  }

  /**
   * Handles on close component event.
   *
   * @param {object} event - Event object.
   */
  onComponentClose(event) {
    const {target} = event;
    const carousel = target.closest(Selectors.CAROUSEL);
    event.preventDefault();
    event.stopPropagation();

    if (target.matches('.js-closeSliderCarousel') && carousel) {
      const componentHeight = carousel.offsetHeight;

      carousel.classList.add(Classes.HIDDEN);
      scrollTo(window.pageYOffset - componentHeight);
      rvpManager.removeAllProductFromStore();
    }
  }

  /**
   * Bind generic events.
   */
  bindEvents() {
    document.jq
      .on(EventTypes.RECENTLY_VIEWED_PRODUCTS_REMOVE_REQUEST, (event, product) =>
        this.onRemoveProductRequest(event, product)
      )
      .on(EventTypes.RECENTLY_VIEWED_PRODUCTS_ADD_REQUEST, (event, data) =>
        this.onAddProductRequest(event, data)
      )
      .one(EventTypes.RECENTLY_VIEWED_PRODUCTS_RENDER_REQUEST, (event, data) => {
        onWindowLoaded(() => {
          this.onRenderRequest(event, data);
        });
      })
      .one(
        EventTypes.RECENTLY_VIEWED_PRODUCTS_RENDER_SUCCESS,
        FiberRanking.renderStickerForProductTiles.bind(this, Selectors.CONTAINER)
      );

    this.container.jq.one('vclick', Selectors.CLOSE_COMPONENT, event =>
      this.onComponentClose(event)
    );
  }

  /**
   * Handles on remove product request events.
   *
   * @param {object} event - Event object.
   * @param {object} product - Product object.
   */
  onRemoveProductRequest(event, product) {
    rvpManager.removeProductFromStore(product?.sku);
  }

  /**
   * Handles on add product request events.
   *
   * @param {object} event - Event object.
   * @param {object} data - Event data.
   */
  onAddProductRequest(event, data = {}) {
    const product = data.productData;
    rvpManager.addProductToStore(product);

    if (data.renderView !== false) {
      const products = this.getProducts(this.initialRenderPerformed ? product : null);
      this.render(products);
    }
  }

  /**
   * Handles on render request events.
   *
   * @param {object} event - Event object.
   * @param {object} data - Data object.
   */
  onRenderRequest(event, data = {}) {
    const products = this.getProducts(null, data.excludeSku?.trim(), data.excludeLast);

    if (!products.length) {
      this.container.classList.add(Classes.HIDDEN);
      return;
    }

    this.render(products);
  }

  /**
   * Handles on remove product click events.
   *
   * @param {object} event - Event object.
   */
  onRemoveProductClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const $product = $(event.target).closest(Selectors.PRODUCT);
    const $recentlyViewedProducts = this.container.jq;
    const $slideCarousel = $recentlyViewedProducts.find(Selectors.CAROUSEL);
    const hasSiblings = $product.siblings().length;
    const h = $product.outerHeight();

    rvpManager.removeProductFromStore($product.data('sku'));

    gsap.to($product, {
      duration: 0.4,
      css: {
        opacity: 0,
        marginLeft: -$product.width(),
      },
      onComplete() {
        $product.remove();

        if (!hasSiblings) {
          $recentlyViewedProducts.addClass(Classes.HIDDEN);
          scrollTo(window.pageYOffset - h);
        } else {
          $slideCarousel.trigger(EventTypes.SLIDE_CAROUSEL_RENDER_REQUEST);
        }
      },
    });
  }
}

export default new RecentlyViewedProducts();
